/**
 * Program 12.1 UX3 — Prequalification requirement matrix UI.
 */
import React from 'react';
import { Progress, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  computePrequalCompleteness,
  computePrequalSubmissionReadiness,
  type PrequalRequirementRow,
} from './prequalRequirementMatrix';

const statusColor: Record<string, string> = {
  COMPLETE: 'green',
  IN_REVIEW: 'blue',
  MISSING: 'red',
  NOT_APPLICABLE: 'default',
};

export type EpcPrequalRequirementMatrixProps = {
  rows: PrequalRequirementRow[];
  submissionBlocked?: boolean;
  submissionBlockReason?: string;
};

export const EpcPrequalRequirementMatrix: React.FC<EpcPrequalRequirementMatrixProps> = ({
  rows,
  submissionBlocked,
  submissionBlockReason,
}) => {
  const completeness = computePrequalCompleteness(rows);
  const submission = computePrequalSubmissionReadiness(
    rows,
    Boolean(submissionBlocked),
    submissionBlockReason
  );

  const columns: ColumnsType<PrequalRequirementRow> = [
    { title: 'Section', dataIndex: 'section', width: 120 },
    { title: 'Requirement', dataIndex: 'requirement' },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 120,
      render: (s: string) => <Tag color={statusColor[s] || 'default'}>{s.replace('_', ' ')}</Tag>,
    },
    { title: 'Evidence', dataIndex: 'evidence', width: 140, ellipsis: true },
    { title: 'Owner', dataIndex: 'owner', width: 100 },
    { title: 'Due', dataIndex: 'due_date', width: 100 },
    { title: 'Issue', dataIndex: 'issue', ellipsis: true },
  ];

  return (
    <>
      <Progress
        percent={completeness.completionPct}
        format={() =>
          `${completeness.complete} complete · ${completeness.missing} missing · ${completeness.notApplicable} N/A`
        }
        style={{ marginBottom: 12 }}
      />
      <Tag color={submission.ready ? 'green' : 'orange'}>
        Submission readiness: {submission.ready ? 'READY' : 'NOT READY'}
      </Tag>
      {!submission.ready && submission.blockers.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 12 }}>
          Blocking: {submission.blockers.join(' · ')}
        </div>
      )}
      <Table
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ x: 800 }}
        style={{ marginTop: 12 }}
      />
    </>
  );
};

export default EpcPrequalRequirementMatrix;
